/**
 * Whether a plan's coverage window is still open (endDate >= today).
 *
 * Matches the "closed" rule used by the broker AvailablePlans tab
 * (AvailablePlans.tsx:153) — a plan whose endDate is in the past is
 * considered closed and is hidden from enrollment wizards / plan pickers.
 *
 * Lives in its own .ts module (not co-located with `typeIcons.tsx`) so unit
 * tests can import without pulling lucide-react / React.
 */
export function isPlanCurrentlyAvailable(
  plan: { endDate?: string | Date | null } | null | undefined,
): boolean {
  if (!plan) return false;
  const raw = (plan as any).endDate;
  if (!raw) return true;
  let end: Date;
  if (raw instanceof Date) {
    end = new Date(raw);
  } else {
    const s = String(raw);
    // Date-only strings (YYYY-MM-DD) get parsed as UTC midnight by `new Date()`,
    // which in negative-offset timezones lands on the *previous* calendar day.
    // Anchor at local midnight instead so the "is today" comparison is honest.
    end = /^\d{4}-\d{2}-\d{2}$/.test(s)
      ? new Date(s + "T00:00:00")
      : new Date(s);
  }
  if (Number.isNaN(end.getTime())) return true;
  end.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return end.getTime() >= today.getTime();
}
